import { useEffect, useMemo, useRef, useState } from "react";
import { apiUrl, CarId } from "./protocol";
import { emotionMapRequestTime, emotionMapResponseIsCurrent } from "./emotion-map-data";
import {
  decodeLegacyEmotionGraph,
  LEGACY_EMOTION_CELLS,
  LEGACY_EMOTION_LINKS,
  type EncodedLegacyGraphState,
  type LegacyGraphResult,
} from "./emotion-map-topology";
import {
  measureEmotionSynchronization,
  SynchronizationRateSmoother,
  type EmotionSynchronizationMeasurement,
} from "./emotion-synchronization";
import emotionLabelTranslationConfig from "../../../config/emotion-label-translations.json";
import { useAppLanguage } from "./i18n";
import "./emotion-map.css";

export const EMOTION_DIRECTION_DISPLAY = [
  ["DA", "確信", -90],
  ["OP", "安定", -30],
  ["NA", "違和感", 30],
  ["Ach", "集中", 90],
  ["VP", "切迫", 150],
  ["5-HT", "平静", 210],
] as const;

type IndicatorName = typeof EMOTION_DIRECTION_DISPLAY[number][0];
type DirectionValues = Record<IndicatorName, number>;

export interface EmotionMapOriginState {
  cells: DirectionValues;
  connections: DirectionValues;
  updatedAtMs: number | null;
  lastTriggerStrength: number;
  decay: {
    mode: "driven" | "decaying";
    coefficient: number;
    elapsedSinceUpdateMs: number | null;
  };
}

export interface EmotionMapIndividualState {
  indicators: DirectionValues;
  mapAction: DirectionValues;
  attenuation: { da: number; naFast: number; naSlow: number };
  situation: EmotionMapOriginState;
  reaction: EmotionMapOriginState;
  legacy_graph: EncodedLegacyGraphState;
}

export interface EmotionMapRecord {
  schema_version: "observation-emotion-map-state/v2";
  car_id: CarId;
  tick_index: number;
  race_time_ms: number;
  causal_id: string;
  learning_used: false;
  driver: EmotionMapIndividualState;
  vehicle: EmotionMapIndividualState;
  trigger_strengths: {
    driver: { situation: number; reaction: number };
    vehicle: { situation: number; reaction: number };
  };
}

export interface EmotionMapSynchronizationSnapshot {
  ratePercent: number;
  measurement: EmotionSynchronizationMeasurement;
}

interface EmotionMapResponse {
  carId: CarId;
  seekGeneration: number;
  requestedAtMs: number;
  requestRevision: number;
  record: EmotionMapRecord;
}

const indicatorNames = new Set(EMOTION_DIRECTION_DISPLAY.map(([name]) => name));

function validValues(value: unknown): value is DirectionValues {
  if (!value || typeof value !== "object") return false;
  const values = value as Record<string, unknown>;
  return [...indicatorNames].every(name => Number.isFinite(values[name])
    && Number(values[name]) >= 0 && Number(values[name]) <= 1);
}

function validOrigin(value: unknown): value is EmotionMapOriginState {
  if (!value || typeof value !== "object") return false;
  const origin = value as Partial<EmotionMapOriginState>;
  return validValues(origin.cells) && validValues(origin.connections)
    && (origin.updatedAtMs === null || Number.isInteger(origin.updatedAtMs))
    && Number.isFinite(origin.lastTriggerStrength)
    && !!origin.decay && (origin.decay.mode === "driven" || origin.decay.mode === "decaying")
    && Number.isFinite(origin.decay.coefficient)
    && (origin.decay.elapsedSinceUpdateMs === null || Number.isFinite(origin.decay.elapsedSinceUpdateMs));
}

function validIndividual(value: unknown): value is EmotionMapIndividualState {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<EmotionMapIndividualState>;
  return validValues(item.indicators) && validValues(item.mapAction)
    && validOrigin(item.situation) && validOrigin(item.reaction)
    && !!item.legacy_graph && decodeLegacyEmotionGraph(item.legacy_graph) !== null
    && !!item.attenuation && [item.attenuation.da, item.attenuation.naFast, item.attenuation.naSlow]
      .every(number => Number.isFinite(number) && Number(number) >= 0 && Number(number) <= 1);
}

export function isEmotionMapRecord(value: unknown): value is EmotionMapRecord {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<EmotionMapRecord>;
  return item.schema_version === "observation-emotion-map-state/v2"
    && (item.car_id === "car1" || item.car_id === "car6" || item.car_id === "car14")
    && Number.isInteger(item.tick_index) && Number(item.tick_index) >= 0
    && Number.isInteger(item.race_time_ms)
    && item.causal_id === `emotion-map/${item.car_id}/tick/${item.tick_index}`
    && item.learning_used === false
    && validIndividual(item.driver) && validIndividual(item.vehicle);
}

export function latestEmotionMapRecord(
  records: EmotionMapRecord[], carId: CarId, raceTimeMs: number,
): EmotionMapRecord | null {
  if (!records.length || records[0].car_id !== carId || raceTimeMs < records[0].race_time_ms) return null;
  let low = 0;
  let high = records.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (records[middle].race_time_ms <= raceTimeMs) low = middle;
    else high = middle - 1;
  }
  return records[low].car_id === carId ? records[low] : null;
}

export function useEmotionMapRecord(
  carId: CarId, raceTimeMs: number, seekGeneration: number, dataFetchEnabled = true,
): {
  response: EmotionMapResponse | null;
  status: "loading" | "ready" | "error";
  error: string;
} {
  const requestedAtMs = emotionMapRequestTime(raceTimeMs, carId);
  const [responseState, setResponseState] = useState<EmotionMapResponse | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [error, setError] = useState("");
  const revision = useRef(0);
  const responseRef = useRef<EmotionMapResponse | null>(null);

  useEffect(() => {
    const requestRevision = revision.current + 1;
    revision.current = requestRevision;
    const controller = new AbortController();
    if (!dataFetchEnabled) return () => controller.abort();
    const retained = responseRef.current?.carId === carId
      && responseRef.current.seekGeneration === seekGeneration;
    if (!retained) {
      responseRef.current = null;
      setResponseState(null);
      setStatus("loading");
    }
    setError("");
    fetch(apiUrl("/api/race/emotion-map", {
      car_id: carId, at_ms: requestedAtMs, seek_generation: seekGeneration,
    }), { cache: "no-store", signal: controller.signal })
      .then(async response => {
        if (!response.ok) throw new Error(`emotion map series request failed: ${response.status}`);
        if (response.headers.get("x-viewer-seek-generation") !== String(seekGeneration))
          throw new Error("emotion map response generation differs");
        const record = await response.json() as unknown;
        if (!isEmotionMapRecord(record) || record.car_id !== carId || record.race_time_ms > requestedAtMs)
          throw new Error("emotion map response identity differs");
        const currentIdentity = { carId, seekGeneration, requestedAtMs, requestRevision: revision.current };
        const responseIdentity = { carId: record.car_id, seekGeneration, requestedAtMs, requestRevision };
        if (!controller.signal.aborted && emotionMapResponseIsCurrent(currentIdentity, responseIdentity)) {
          const accepted = {
            carId, seekGeneration, requestedAtMs, requestRevision, record,
          };
          responseRef.current = accepted;
          setResponseState(accepted);
          setStatus("ready");
        }
      }).catch(reason => {
        if (reason?.name !== "AbortError" && revision.current === requestRevision) {
          setError(String(reason));
          setStatus("error");
        }
      });
    return () => controller.abort();
  }, [carId, dataFetchEnabled, requestedAtMs, seekGeneration]);

  return { response: responseState, status, error };
}

const MANDALA_SIZE = 800;
const MANDALA_CENTER = MANDALA_SIZE / 2;
const MANDALA_RADIUS = MANDALA_SIZE * .45;
const MANDALA_MAX_LAYER = 9;
const TAP_INTERACTION_QUERY = "(max-width: 1180px), (any-pointer: coarse)";
const LAYER_TYPE: Record<number, { fontSize: number; fontSetback: number }> = {
  [-1]: { fontSize: 16, fontSetback: 24 },
  0: { fontSize: 16, fontSetback: 24 },
  1: { fontSize: 14, fontSetback: 26 },
  2: { fontSize: 18, fontSetback: 26 },
  3: { fontSize: 18, fontSetback: 26 },
  4: { fontSize: 18, fontSetback: 26 },
  5: { fontSize: 16, fontSetback: 26 },
  6: { fontSize: 18, fontSetback: 26 },
  7: { fontSize: 18, fontSetback: 26 },
};

function individualGraph(individual: EmotionMapIndividualState): LegacyGraphResult {
  return decodeLegacyEmotionGraph(individual.legacy_graph) ?? {
    membranePotentials: Array.from({ length: LEGACY_EMOTION_CELLS.length }, () => 0),
    linkWeights: Object.fromEntries(LEGACY_EMOTION_LINKS.map(link => [link.id, 0])),
    boosts: { ENK: 0, END: 0, OXY: 0, "DA>T": 0, "DA<T": 0, NA: 0, VP: 0, ACH: 0,
      "5HT>T": 0, "5HT<T": 0, "5HT=0": 0 },
  };
}

function traceMandalaCell(
  context: CanvasRenderingContext2D,
  layer: number,
  startAngle: number,
  endAngle: number,
) {
  const unit = MANDALA_RADIUS / MANDALA_MAX_LAYER;
  const outerRadius = unit * (layer + 2);
  const innerRadius = Math.max(0, unit * (layer + 1));
  context.beginPath();
  context.arc(MANDALA_CENTER, MANDALA_CENTER, outerRadius, startAngle, endAngle, false);
  if (innerRadius > 0) {
    context.lineTo(
      MANDALA_CENTER + innerRadius * Math.cos(endAngle),
      MANDALA_CENTER + innerRadius * Math.sin(endAngle),
    );
    context.arc(MANDALA_CENTER, MANDALA_CENTER, innerRadius, endAngle, startAngle, true);
  } else {
    context.lineTo(MANDALA_CENTER, MANDALA_CENTER);
  }
  context.closePath();
}

function drawMandalaFill(
  context: CanvasRenderingContext2D,
  layer: number,
  startAngle: number,
  endAngle: number,
  fillStyle: string,
) {
  traceMandalaCell(context, layer, startAngle, endAngle);
  context.fillStyle = fillStyle;
  context.fill();
}

function drawMandalaOutlineAndLabel(
  context: CanvasRenderingContext2D,
  layer: number,
  startAngle: number,
  endAngle: number,
  label: string,
) {
  const type = LAYER_TYPE[layer];
  const radius = MANDALA_RADIUS / MANDALA_MAX_LAYER * (layer + 2);
  traceMandalaCell(context, layer, startAngle, endAngle);
  context.strokeStyle = "black";
  context.lineWidth = 1;
  context.stroke();
  const labelAngle = startAngle + (endAngle - startAngle) / 2;
  const labelX = MANDALA_CENTER + (radius - type.fontSetback) * Math.cos(labelAngle);
  const labelY = MANDALA_CENTER + (radius - type.fontSetback) * Math.sin(labelAngle);
  context.save();
  context.translate(labelX, labelY);
  context.rotate(Math.PI / 2 + labelAngle);
  context.fillStyle = "black";
  context.font = `${type.fontSize}px 'ＭＳ ゴシック', sans-serif`;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(label, 0, 0);
  context.restore();
}

function drawMandalaAxisLabels(context: CanvasRenderingContext2D, labels: {
  pleasant: string;
  unpleasant: string;
  situation: string;
  reaction: string;
}) {
  const axisCenter = MANDALA_CENTER;
  const verticalAxisPosition = MANDALA_CENTER + MANDALA_RADIUS + 14;
  const oppositeVerticalAxisPosition = MANDALA_CENTER - MANDALA_RADIUS - 14;
  const horizontalAxisPosition = MANDALA_CENTER + MANDALA_RADIUS + 18;
  const oppositeHorizontalAxisPosition = MANDALA_CENTER - MANDALA_RADIUS - 18;
  const draw = (label: string, x: number, y: number, rotation = 0) => {
    context.save();
    context.translate(x, y);
    context.rotate(rotation);
    context.fillStyle = "#fff";
    context.font = "700 24px Arial, sans-serif";
    context.textAlign = "center";
    context.textBaseline = "middle";
    context.shadowColor = "#000";
    context.shadowBlur = 4;
    context.shadowOffsetY = 2;
    context.fillText(label, 0, 0);
    context.restore();
  };
  draw(labels.pleasant, axisCenter, oppositeVerticalAxisPosition);
  draw(labels.unpleasant, axisCenter, verticalAxisPosition);
  draw(labels.situation, horizontalAxisPosition, axisCenter, Math.PI / 2);
  draw(labels.reaction, oppositeHorizontalAxisPosition, axisCenter, -Math.PI / 2);
}

function mandalaLayerStrengths(driverPotential: number, vehiclePotential: number) {
  const driver = Math.max(0, Math.min(1, driverPotential));
  const vehicle = Math.max(0, Math.min(1, vehiclePotential));
  const sync = Math.min(driver, vehicle);
  return {
    driverOnly: Math.max(0, driver - vehicle),
    vehicleOnly: Math.max(0, vehicle - driver),
    sync,
  };
}

function EmotionMandala({ driver, vehicle }: {
  driver: EmotionMapIndividualState;
  vehicle: EmotionMapIndividualState;
}) {
  const { language, t } = useAppLanguage();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredCell, setHoveredCell] = useState<{ cellId: number; left: number; top: number } | null>(null);
  const [tapPinnedCellId, setTapPinnedCellId] = useState<number | null>(null);
  const driverGraph = useMemo(() => individualGraph(driver), [driver]);
  const vehicleGraph = useMemo(() => individualGraph(vehicle), [vehicle]);
  const translations = emotionLabelTranslationConfig.cells as {
    cell_id: number; ja: string; en: string; es: string; fr: string; zh: string;
  }[];
  const translationKey = language === "ja-JP" ? "ja" : language === "en-US" ? "en"
    : language === "es-ES" ? "es" : language === "fr-FR" ? "fr" : "zh";
  const translatedLabel = hoveredCell
    ? translations.find(item => item.cell_id === hoveredCell.cellId)?.[translationKey]
    : null;
  useEffect(() => {
    const context = canvasRef.current?.getContext("2d");
    if (!context) return;
    context.clearRect(0, 0, MANDALA_SIZE, MANDALA_SIZE);
    for (const cell of LEGACY_EMOTION_CELLS)
      drawMandalaFill(context, cell.layer, cell.start, cell.end, "rgb(240, 240, 240)");
    for (const cell of LEGACY_EMOTION_CELLS) {
      const strengths = mandalaLayerStrengths(
        driverGraph.membranePotentials[cell.id], vehicleGraph.membranePotentials[cell.id],
      );
      const alpha = strengths.driverOnly * .82;
      if (alpha > 0)
        drawMandalaFill(context, cell.layer, cell.start, cell.end, `rgba(65, 105, 255, ${alpha})`);
    }
    for (const cell of LEGACY_EMOTION_CELLS) {
      const strengths = mandalaLayerStrengths(
        driverGraph.membranePotentials[cell.id], vehicleGraph.membranePotentials[cell.id],
      );
      const alpha = strengths.vehicleOnly * .65;
      if (alpha > 0)
        drawMandalaFill(context, cell.layer, cell.start, cell.end, `rgba(255, 75, 85, ${alpha})`);
    }
    for (const cell of LEGACY_EMOTION_CELLS) {
      const strengths = mandalaLayerStrengths(
        driverGraph.membranePotentials[cell.id], vehicleGraph.membranePotentials[cell.id],
      );
      const alpha = strengths.sync * .9;
      if (alpha > 0)
        drawMandalaFill(context, cell.layer, cell.start, cell.end, `rgba(168, 85, 247, ${alpha})`);
    }
    for (const cell of LEGACY_EMOTION_CELLS) {
      drawMandalaOutlineAndLabel(context, cell.layer, cell.start, cell.end, cell.label);
    }
    drawMandalaAxisLabels(context, {
      pleasant: t("emotion.axisPleasant"),
      unpleasant: t("emotion.axisUnpleasant"),
      situation: t("emotion.axisSituation"),
      reaction: t("emotion.axisReaction"),
    });
  }, [driverGraph, t, vehicleGraph]);
  useEffect(() => {
    if (tapPinnedCellId === null) return;
    const mediaQuery = window.matchMedia(TAP_INTERACTION_QUERY);
    const clearPinnedCell = () => {
      setTapPinnedCellId(null);
      setHoveredCell(null);
    };
    const dismissOutsideMap = (event: PointerEvent) => {
      if (!canvasRef.current?.contains(event.target as Node)) clearPinnedCell();
    };
    const dismissOutsideTapInteraction = (event: MediaQueryListEvent) => {
      if (!event.matches) clearPinnedCell();
    };
    window.addEventListener("pointerdown", dismissOutsideMap);
    mediaQuery.addEventListener("change", dismissOutsideTapInteraction);
    return () => {
      window.removeEventListener("pointerdown", dismissOutsideMap);
      mediaQuery.removeEventListener("change", dismissOutsideTapInteraction);
    };
  }, [tapPinnedCellId]);
  const locateCell = (clientX: number, clientY: number, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    const renderedSize = Math.min(rect.width, rect.height);
    const offsetX = (rect.width - renderedSize) / 2;
    const offsetY = (rect.height - renderedSize) / 2;
    const x = (clientX - rect.left - offsetX) * MANDALA_SIZE / renderedSize;
    const y = (clientY - rect.top - offsetY) * MANDALA_SIZE / renderedSize;
    const radius = Math.hypot(x - MANDALA_CENTER, y - MANDALA_CENTER);
    const layer = Math.floor(radius / (MANDALA_RADIUS / MANDALA_MAX_LAYER)) - 1;
    let angle = Math.atan2(y - MANDALA_CENTER, x - MANDALA_CENTER);
    if (angle < 0) angle += Math.PI * 2;
    const cell = LEGACY_EMOTION_CELLS.find(item => item.layer === layer
      && [angle - Math.PI * 2, angle, angle + Math.PI * 2]
        .some(candidate => candidate >= item.start && candidate <= item.end));
    return cell ? {
      cellId: cell.id,
      left: Math.max(4, Math.min(rect.width - 4, clientX - rect.left + 10)),
      top: Math.max(4, Math.min(rect.height - 4, clientY - rect.top + 10)),
    } : null;
  };
  const updateMouseHover = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (event.pointerType !== "mouse" || tapPinnedCellId !== null) return;
    setHoveredCell(locateCell(event.clientX, event.clientY, event.currentTarget));
  };
  const toggleMobileTap = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!window.matchMedia(TAP_INTERACTION_QUERY).matches) return;
    const selectedCell = locateCell(event.clientX, event.clientY, event.currentTarget);
    if (!selectedCell || selectedCell.cellId === tapPinnedCellId) {
      setTapPinnedCellId(null);
      setHoveredCell(null);
      return;
    }
    setTapPinnedCellId(selectedCell.cellId);
    setHoveredCell(selectedCell);
  };
  return <div className="emotion-mandala-wrap">
    <canvas
      ref={canvasRef}
      className="emotion-mandala"
      width={MANDALA_SIZE}
      height={MANDALA_SIZE}
      role="img"
      aria-label={t("emotion.aria")}
      data-cell-count={LEGACY_EMOTION_CELLS.length}
      data-hovered-cell-id={hoveredCell?.cellId ?? ""}
      data-tap-pinned-cell-id={tapPinnedCellId ?? ""}
      onPointerMove={updateMouseHover}
      onPointerLeave={event => {
        if (event.pointerType === "mouse" && tapPinnedCellId === null) setHoveredCell(null);
      }}
      onClick={toggleMobileTap}
    />
    {hoveredCell && <span
      className="emotion-cell-tooltip"
      role="tooltip"
      lang={language.split("-")[0]}
      style={{ left: hoveredCell.left, top: hoveredCell.top }}
      data-cell-id={hoveredCell.cellId}
    >{translatedLabel ?? t("emotion.tooltipUnavailable")}</span>}
  </div>;
}

const BAR_WIDTH = 100;
function HormoneBars({ title, values, kind }: { title: string; values: DirectionValues; kind: "driver" | "vehicle" }) {
  return <figure className="emotion-hormone-group" data-kind={kind}>
    <figcaption>{title}</figcaption>
    <svg viewBox="0 0 140 96" role="img" aria-label={`${title} DA、OP、NA、Ach、VP、5-HT`}>
      {EMOTION_DIRECTION_DISPLAY.map(([name], index) => {
        const ratio = Math.max(-1, Math.min(1, values[name]));
        const fillWidth = BAR_WIDTH * (0.5 * ratio + 0.5);
        const y = index * 16;
        return <g key={name} transform={`translate(0 ${y})`} data-indicator={name} data-value={ratio.toFixed(6)}>
          <text x="0" y="10">{name}</text>
          <rect className="emotion-hormone-fill" x="40" y="0" width={fillWidth} height="12" />
          <rect className="emotion-hormone-frame" x="40" y="0" width="100" height="12" />
          <line className="emotion-hormone-zero" x1="90" y1="0" x2="90" y2="12" />
        </g>;
      })}
    </svg>
  </figure>;
}

export function EmotionMapPanel({
  carId,
  raceTimeMs,
  seekGeneration,
  dataFetchEnabled = true,
  onCurrentRecord,
}: {
  carId: CarId;
  raceTimeMs: number;
  seekGeneration: number;
  dataFetchEnabled?: boolean;
  onCurrentRecord?: (
    record: EmotionMapRecord | null,
    synchronization: EmotionMapSynchronizationSnapshot | null,
  ) => void;
}) {
  const { t, formatNumber } = useAppLanguage();
  const synchronizationSmoother = useRef(new SynchronizationRateSmoother(5));
  const source = useEmotionMapRecord(carId, raceTimeMs, seekGeneration, dataFetchEnabled);
  const record = useMemo(() => source.response?.carId === carId
    && source.response.seekGeneration === seekGeneration ? source.response.record : null,
  [carId, seekGeneration, source.response]);
  const ready = source.status === "ready" && record?.car_id === carId
    && source.response?.seekGeneration === seekGeneration;
  const synchronization = useMemo(() => ready && record
    ? measureEmotionSynchronization(record.driver.legacy_graph, record.vehicle.legacy_graph)
    : null, [ready, record]);
  const synchronizationRate = synchronization && record
    ? synchronizationSmoother.current.update(
      `${carId}/${seekGeneration}`,
      record.causal_id,
      synchronization.raw_rate_percent,
    )
    : 50;
  useEffect(() => {
    onCurrentRecord?.(
      ready ? record : null,
      ready && synchronization ? { ratePercent: synchronizationRate, measurement: synchronization } : null,
    );
  }, [onCurrentRecord, ready, record, synchronization, synchronizationRate]);
  return (
    <section
      className="emotion-map-panel"
      aria-label={t("emotion.aria")}
      data-status={source.status}
      data-car-id={carId}
      data-seek-generation={seekGeneration}
      data-causal-id={ready ? record.causal_id : ""}
      data-tick={ready ? record.tick_index : ""}
    >
      <header>
        <div><span>OBSERVATION EMOTION MAP</span><h2>DRIVER × VEHICLE EMOTION</h2></div>
        <div className="emotion-legend"><span>{t("emotion.driver")}</span><span>{t("emotion.vehicle")}</span><span>{t("emotion.sync")}</span></div>
      </header>
      {source.error && <p className="emotion-map-error" role="alert">{t("emotion.loadError")}: {source.error}</p>}
      {ready ? <>
        <div className="emotion-map-stage">
          <EmotionMandala driver={record.driver} vehicle={record.vehicle} />
          <aside className="emotion-hormone-bars" aria-label={t("emotion.indicators")}>
            <HormoneBars title={t("emotion.driverIndicators")} values={record.driver.indicators} kind="driver" />
            <HormoneBars title={t("emotion.vehicleIndicators")} values={record.vehicle.indicators} kind="vehicle" />
          </aside>
          <div
            className="sync-rate"
            data-testid="synchronization-rate"
            data-rate-percent={synchronizationRate.toFixed(3)}
            data-raw-rate-percent={synchronization?.raw_rate_percent.toFixed(3)}
            data-alignment={synchronization?.alignment.toFixed(6)}
          >
            {t("emotion.syncRate")}: {formatNumber(Math.round(synchronizationRate))}%
          </div>
        </div>
        <footer>
          <span>{t("emotion.origin")}</span>
          <span>{formatNumber(LEGACY_EMOTION_CELLS.length)} {t("emotion.cellsMandala")}</span>
          <span>tick {formatNumber(record.tick_index)}</span>
        </footer>
      </> : <p className="emotion-map-loading">{t("emotion.loading")}</p>}
    </section>
  );
}
